import React, { useState, useEffect } from "react";
import Page from "../shared/components/PageComponent";
import Layout from "../shared/components/LayoutComponent";
import {
  Alert,
  AlertTitle,
  Backdrop,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { VorgabenDialog } from "./VorgabenDialog";
import KabelEditDialog from "./KabelEditDialog";
import apiRequest from "../../functions/api";
import { useMessage } from "../../context/message-context";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../constants/apiConstants";

// Icons
import CableIcon from "@mui/icons-material/Cable";
import DevicesIcon from "@mui/icons-material/Devices";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ReplyAllIcon from "@mui/icons-material/ReplyAll";
import RuleIcon from "@mui/icons-material/Rule";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { ExcelExportDialog } from "./ExcelExportDialog";
import openFile from "../shared/functions/download";
import { KabelFastDialog } from "./KabelFastDialog";

const program = "KMPAPI";

function KabelManagement(props) {
  const { user, updateTitle, accessToken, client } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showErrorMessage } = useMessage();

  const [optionen, setOptionen] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openFastDialog, setOpenFastDialog] = useState(false);
  const [openVorgaben, setOpenVorgaben] = useState(false);
  const [vorgaben, setVorgaben] = useState(false);
  const [keineVorgaben, setKeineVorgaben] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [excelDialog, setExcelDialog] = useState(false);

  // ----------------------------------------------------
  // -----------Allgemeine funktionen--------------------
  // ----------------------------------------------------

  useEffect(() => {
    setIsLoading(true);
    initVorgaben();
    getOptions();
  }, []);

  const getOptions = () => {
    const headers = {
      Authorization: "Bearer " + accessToken,
    };

    const payload = {
      mandant: client,
      program: program,
      statement: "KABEL_OPTIONEN",
    };

    apiRequest("POST", "daten", payload, headers, optionCb, errorCb);
  };

  const optionCb = (response) => {
    if (response.status === 200) {
      setOptionen(response.data);
    } else {
      showErrorMessage(response.data.message);
    }
  };

  const initVorgaben = () => {
    const headers = {
      Authorization: "Bearer " + accessToken,
    };

    const payload = {
      mandant: client,
      program: program,
      statement: "VORGABEN_HOLEN",
    };

    apiRequest("POST", "daten", payload, headers, vorgabenCb, errorCb);
  };

  const vorgabenCb = (response) => {
    setIsLoading(false);
    if (response.status === 200) {
      setVorgaben(response.data?.vorgaben);

      // Prüfen ob wir Vorgaben zurückbekommen
      const hatVorgaben = response.data?.vorgaben?.liste.every((item) => (response.data.vorgaben[item]?.length ?? 0) > 0);
      if (!hatVorgaben) {
        setKeineVorgaben(true);
      } else {
        setKeineVorgaben(false);
      }
    } else {
      showErrorMessage(response.data.message);
    }
  };

  const reloadVorgaben = () => {
    initVorgaben();
  };

  const errorCb = () => {
    showErrorMessage(t("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut."));
  };

  const endeVorgaben = () => {
    setOpenVorgaben(false);
    getOptions();
  };

  const closeDialog = () => {
    getOptions();
    setOpenFastDialog(false);
    setOpenDialog(false);
  };

  // ----------------------------------------------------
  // -------------Handy funktionen-----------------------
  // ----------------------------------------------------
  const MobileMain = () => {
    return (
      <>
        <Box height={"50px"}></Box>
        <Box sx={{ paddingLeft: 2 }} display="flex" alignItems={"start"}>
          <Tooltip title={t("Zum Verteiler")}>
            <IconButton color="primary" onClick={() => navigate("/")}>
              <ReplyAllIcon />
              <Typography sx={{ paddingLeft: 1 }}>Verteiler</Typography>
            </IconButton>
          </Tooltip>
        </Box>
        <List>
          <Divider />
          <ListItem>
            <ListItemButton disabled={keineVorgaben} onClick={() => navigate("/KMPAPI/kabel")}>
              <ListItemIcon>
                <SearchIcon color="primary" />
                <CableIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Nach Kabel suchen" />
            </ListItemButton>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemButton disabled={keineVorgaben} onClick={() => navigate("/KMPAPI/device")}>
              <ListItemIcon>
                <SearchIcon color="primary" />
                <DevicesIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Nach Gerät suchen" />
            </ListItemButton>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemButton disabled={keineVorgaben} onClick={() => setOpenDialog(true)}>
              <ListItemIcon>
                <AddIcon color="primary" />
                <CableIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Neues Kabel anlegen" />
            </ListItemButton>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemButton disabled={keineVorgaben} onClick={() => setOpenFastDialog(true)}>
              <ListItemIcon>
                <AddIcon color="primary" />
                <CableIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Schnellmodus mehrere Kabel" />
            </ListItemButton>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemButton onClick={() => setOpenVorgaben(true)}>
              <ListItemIcon>
                <RuleIcon color="primary" />
                <AddIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Vorgaben Verwalten" />
            </ListItemButton>
          </ListItem>
          <Divider />
        </List>
        {keineVorgaben && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>{t("Einrichtung erforderlich")}</AlertTitle>
            {t(
              "Bevor das Programm verwendet werden kann, müssen alle Vorgaben über „Vorgaben Verwalten“ angelegt werden. Jede Vorgabe muss dabei mindestens einen Eintrag enthalten, da sie als Stammdaten für die Auswahl in der Anwendung dienen.",
            )}
          </Alert>
        )}
      </>
    );
  };

  // ----------------------------------------------------
  // -------------Desktop funktionen---------------------
  // ----------------------------------------------------

  const excelExport = (kabel, device) => {
    if (kabel == false && device == false) {
      showErrorMessage("Eine der beiden Auswertungen muss mindestens angewählt werden");
      return;
    }
    setIsLoading(true);

    const payload = {
      mandant: client,
      program: program,
      statement: "EXCEL_EXPORT",
      KMPAPI_KABEL: kabel,
      KMPAPI_DEVICES: device,
    };

    const headers = {
      Authorization: "Bearer " + accessToken,
      Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    };

    axios
      .post(API_BASE_URL + "daten", payload, { headers: headers, responseType: "blob" })
      .then((response) => {
        setExcelDialog(false);
        setIsLoading(false);
        const disposition = response.headers["content-disposition"];
        let fileName = "export.xlsx";
        if (disposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i;
          const matches = filenameRegex.exec(disposition);

          if (matches && matches[1]) {
            fileName = matches[1].replace(/['"]/g, "");
          }
        }

        if (!fileName.toLowerCase().endsWith(".xlsx") && !fileName.toLowerCase().endsWith(".xls")) {
          fileName = fileName + ".xlsx";
        }

        const contentFile = response.data;
        openFile(fileName, contentFile);
      })
      .catch(function (error) {
        setIsLoading(false);
        console.log(error);
        showErrorMessage(t("Ein Fehler ist aufgetreten. Bitte versuchen Sie den Download erneut."));
      });
  };

  const DesktopMain = () => {
    return (
      <>
        <Box height={"50px"}></Box>
        <Box sx={{ paddingLeft: 2 }} display="flex" alignItems={"start"}>
          <Tooltip title={t("Zum Verteiler")}>
            <IconButton color="primary" onClick={() => navigate("/")}>
              <ReplyAllIcon />
              <Typography sx={{ paddingLeft: 1 }}>Verteiler</Typography>
            </IconButton>
          </Tooltip>
        </Box>
        <List>
          <Divider />
          <ListItem>
            <ListItemButton disabled={keineVorgaben} onClick={() => navigate("/KMPAPI/kabel")}>
              <ListItemIcon>
                <SearchIcon color="primary" />
                <CableIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Nach Kabel suchen" />
            </ListItemButton>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemButton disabled={keineVorgaben} onClick={() => navigate("/KMPAPI/device")}>
              <ListItemIcon>
                <SearchIcon color="primary" />
                <DevicesIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Nach Gerät suchen" />
            </ListItemButton>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemButton disabled={keineVorgaben} onClick={() => setOpenDialog(true)}>
              <ListItemIcon>
                <AddIcon color="primary" />
                <CableIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Neues Kabel anlegen" />
            </ListItemButton>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemButton disabled={keineVorgaben} onClick={() => setOpenFastDialog(true)}>
              <ListItemIcon>
                <AddIcon color="primary" />
                <CableIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Schnellmodus mehrere Kabel" />
            </ListItemButton>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemButton onClick={() => setOpenVorgaben(true)}>
              <ListItemIcon>
                <RuleIcon color="primary" />
                <AddIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Vorgaben Verwalten" />
            </ListItemButton>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemButton onClick={() => setExcelDialog(true)}>
              <ListItemIcon>
                <FileDownloadIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Excel Export starten" />
            </ListItemButton>
          </ListItem>
          <Divider />
        </List>
        {keineVorgaben && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>{t("Einrichtung erforderlich")}</AlertTitle>
            {t(
              "Bevor das Programm verwendet werden kann, müssen alle Vorgaben über „Vorgaben Verwalten“ angelegt werden. Jede Vorgabe muss dabei mindestens einen Eintrag enthalten, da sie als Stammdaten für die Auswahl in der Anwendung dienen.",
            )}
          </Alert>
        )}
      </>
    );
  };

  if (isLoading) {
    return (
      <Backdrop
        open
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="inherit" />
          <Typography>{t("Daten werden geladen...")}</Typography>
        </Stack>
      </Backdrop>
    );
  }

  return (
    <Page>
      <Layout>
        {/* Desktop */}
        <Box sx={{ display: { xs: "none", sm: "none", md: "block" }, width: "100%" }}>
          <DesktopMain />
        </Box>

        {/* Mobile */}
        <Box sx={{ display: { xs: "block", sm: "block", md: "none" } }}>
          <MobileMain />
        </Box>

        <KabelEditDialog accessToken={accessToken} client={client} optionen={optionen} dialogVersion={2} open={openDialog} handleClose={() => closeDialog()} />

        <KabelFastDialog
          accessToken={accessToken}
          client={client}
          optionen={optionen}
          dialogVersion={2}
          open={openFastDialog}
          handleClose={() => closeDialog()}
        />

        <VorgabenDialog
          open={openVorgaben}
          handleClose={() => endeVorgaben()}
          vorgaben={vorgaben}
          reloadVorgaben={reloadVorgaben}
          accessToken={accessToken}
          client={client}
        />

        <ExcelExportDialog open={excelDialog} handleClose={() => setExcelDialog(false)} excelExport={excelExport} />
      </Layout>
    </Page>
  );
}

export default KabelManagement;
